/**
 * Client-side image compression + resize helper.
 * Uses Canvas API — no external dependency.
 *
 * Behavior:
 * - Downscales so that max(width, height) <= maxDim (default 1600px).
 * - Re-encodes to JPEG at `quality` (default 0.82) to strip metadata.
 * - Skips resize if the source is already smaller and its size is under `bypassBelowKb`.
 * - Preserves the original filename with a ".jpg" suffix.
 *
 * Returns a File (or the original if no resize was needed / on error).
 */
export async function resizeImage(
  file,
  { maxDim = 1600, quality = 0.82, bypassBelowKb = 350 } = {}
) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // For GIFs / animated / non-standard formats, bail out.
  if (file.type === "image/gif") return file;
  const sizeKb = file.size / 1024;

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = bitmap;
    const longSide = Math.max(width, height);
    // Bypass small images that are already lightweight
    if (longSide <= maxDim && sizeKb <= bypassBelowKb) {
      bitmap.close?.();
      return file;
    }
    const scale = longSide > maxDim ? maxDim / longSide : 1;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;

    // If the compressed result is not meaningfully smaller, keep the original.
    if (blob.size >= file.size) return file;

    const baseName = file.name?.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

async function loadBitmap(file) {
  // Prefer createImageBitmap (fast, off-main-thread) when available.
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch { /* fall through */ }
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(Object.assign(img, {
        width: img.naturalWidth,
        height: img.naturalHeight,
        close: () => {},
      }));
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}
